import {
  CATEGORY_NAME_MAX_LENGTH,
  TICKET_CHANNEL_LABELS,
  categoryNameSchema,
  type ProductCategory,
  type TicketChannel,
} from '@easyhelp/shared';
import AddIcon from '@mui/icons-material/Add';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState, type FormEvent } from 'react';
import { SettingsSection } from '../coda/SettingsSection';

const CHANNEL_DESCRIPTIONS: Record<TicketChannel, string> = {
  'in-piattaforma': 'Le voci proposte agli operatori che segnalano dalla piattaforma, con sessione.',
  'pre-accesso': 'Le voci proposte a chi segnala senza riuscire ad accedere, senza sessione.',
};

export interface CategoriesSectionProps {
  channel: TicketChannel;
  /** Voci attive del set, nell'ordine in cui il prodotto le mostra. */
  categories: ProductCategory[];
  busy?: boolean;
  /** Errore dell'ultima aggiunta su questo canale (es. 409 per duplicato). */
  addError?: string | null;
  /** Errore dell'ultimo spostamento in questo set, mostrato sotto l'elenco. */
  error?: string | null;
  /** Aggiunge una voce; la promessa risolve quando l'API ha accettato (l'errore è in `addError`). */
  onAdd: (name: string) => Promise<unknown>;
  onMove: (category: ProductCategory, position: number) => void;
  onRename: (category: ProductCategory) => void;
  onRemove: (category: ProductCategory) => void;
}

/** Il set di categorie di un canale: aggiunta in coda, riordino con le frecce, rinomina e rimozione. */
export function CategoriesSection({
  channel,
  categories,
  busy = false,
  addError = null,
  error = null,
  onAdd,
  onMove,
  onRename,
  onRemove,
}: CategoriesSectionProps) {
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);
  const label = TICKET_CHANNEL_LABELS[channel];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = categoryNameSchema.safeParse(draft);
    if (!parsed.success) {
      setDraftError(parsed.error.issues[0]?.message ?? 'Nome non valido');
      return;
    }
    setDraftError(null);
    // L'esito negativo arriva al genitore (`addError`): qui si svuota il campo solo se è andata bene.
    void onAdd(parsed.data).then(
      () => setDraft(''),
      () => undefined
    );
  };

  return (
    <SettingsSection
      id={`categories-${channel}-title`}
      title={`Categorie · ${label}`}
      description={CHANNEL_DESCRIPTIONS[channel]}
    >
      {categories.length === 0 ? (
        <Typography color="text.secondary">
          Nessuna categoria: finché il set è vuoto il form del prodotto non ha voci da proporre.
        </Typography>
      ) : (
        <List disablePadding aria-label={`Categorie ${label}`}>
          {categories.map((category, index) => (
            <ListItem
              key={category.id}
              divider
              disableGutters
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Sposta su">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Sposta su ${category.name}`}
                        disabled={busy || index === 0}
                        onClick={() => onMove(category, index - 1)}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Sposta giù">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Sposta giù ${category.name}`}
                        disabled={busy || index === categories.length - 1}
                        onClick={() => onMove(category, index + 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rinomina">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Rinomina ${category.name}`}
                        disabled={busy}
                        onClick={() => onRename(category)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rimuovi">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Rimuovi ${category.name}`}
                        disabled={busy}
                        onClick={() => onRemove(category)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }
              sx={{ pr: 20 }}
            >
              <ListItemText primary={category.name} />
            </ListItem>
          ))}
        </List>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <form onSubmit={submit} noValidate>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label={`Nuova categoria ${label}`}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setDraftError(null);
            }}
            error={Boolean(draftError ?? addError)}
            helperText={draftError ?? addError ?? 'Aggiunta in coda al set; vale per le segnalazioni successive.'}
            slotProps={{ htmlInput: { maxLength: CATEGORY_NAME_MAX_LENGTH } }}
            sx={{ flex: 1 }}
          />
          <Button type="submit" startIcon={<AddIcon />} variant="outlined" disabled={busy} sx={{ mt: { sm: 2 } }}>
            Aggiungi
          </Button>
        </Stack>
      </form>
    </SettingsSection>
  );
}
